import {
  createContext,
  FunctionComponent,
  PropsWithChildren,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { xcss } from '@atlaskit/primitives';
import { type ConsentToken, getConsentContext } from '#definitions/context';
import { isError } from '../../definitions/errors';
import type { Errors } from '#definitions/errors';
import { ConsentInfo, getConsentInfo } from '#definitions/consent-info';
import { useAnalytics } from '../Analytics/AnalyticsProvider';

export type ConsentContext = {
  token: ConsentToken;
  info: ConsentInfo;
  selectedWorkspace?: string | null;
  setSelectedWorkspace?: (workspace: string | null) => void;
  /** When true the shared Footer Accept button is disabled. Set by child flows (e.g. A2A). */
  acceptDisabled?: boolean;
  setAcceptDisabled?: (disabled: boolean) => void;
  selectedOrg?: string | null;
  setSelectedOrg?: (orgId: string | null) => Promise<void>;
  isOrgLoading?: boolean;
  orgError?: Errors | null;
  /**
   * TWG-specific: the user-selected subset of allowed scopes.
   * Undefined while org info is loading; defaults to all filteredScopes (or token.scopes)
   * when an org is selected.
   * The user can deselect individual scopes; this is what gets submitted on Accept.
   */
  selectedScopes?: string[];
  setSelectedScopes?: (scopes: string[]) => void;
};

export const ReactConsentContext = createContext<ConsentContext | null>(null);

/** Shared narrow column for non-MCP consent flows (matches historical App shell). */
export const narrowConsentContentStyle = xcss({
  paddingTop: 'space.400',
  margin: '0 auto',
  maxWidth: '550px',
});

export const useToken = () => {
  const context = useContext(ReactConsentContext);
  if (!context) {
    throw new Error('useToken must be used within context');
  }
  return context.token;
};

export const useInfo = () => {
  const context = useContext(ReactConsentContext);
  if (!context) {
    throw new Error('useInfo must be used within context');
  }
  return context.info;
};

export const useSelectedWorkspace = () => {
  const context = useContext(ReactConsentContext);
  if (!context) {
    throw new Error('useSelectedWorkspace must be used within context');
  }
  return {
    selectedWorkspace: context.selectedWorkspace ?? null,
    setSelectedWorkspace: context.setSelectedWorkspace!,
  };
};

export const useSelectedOrg = () => {
  const context = useContext(ReactConsentContext);
  if (!context) {
    throw new Error('useSelectedOrg must be used within context');
  }
  return {
    selectedOrg: context.selectedOrg ?? null,
    setSelectedOrg: context.setSelectedOrg!,
    isOrgLoading: context.isOrgLoading ?? false,
    availableOrganizations: context.info.availableOrganizations ?? [],
    orgError: context.orgError ?? null,
  };
};

export const ConsentContextProvider: FunctionComponent<
  PropsWithChildren<{ loading: ReactNode }>
> = ({ children, loading }) => {
  const [consentContext, setConsentContext] = useState<{
    token: ConsentToken;
    info: ConsentInfo;
  } | null>(null);
  const [selectedWorkspace, setSelectedWorkspace] = useState<string | null>(
    null,
  );
  const [selectedOrg, setSelectedOrgState] = useState<string | null>(null);
  const [isOrgLoading, setIsOrgLoading] = useState(false);
  const [orgError, setOrgError] = useState<Errors | null>(null);
  const [acceptDisabled, setAcceptDisabled] = useState(false);
  const [selectedScopes, setSelectedScopes] = useState<string[] | undefined>(
    undefined,
  );
  const [isLoading, setLoading] = useState(true);
  const selectedOrgRequestId = useRef(0);
  const { showBoundary } = useErrorBoundary();
  const analyticsClient = useAnalytics();

  useEffect(() => {
    getConsentContext()
      .then(async (token) => {
        if (isError(token)) {
          return showBoundary(token);
        }
        console.log('Parsed token successfully as', token);
        analyticsClient.setUserId(token.userId);
        analyticsClient.setCloudId(token.cloudId);
        analyticsClient.updateState({
          profile: token.profile ?? undefined,
          customer: token.customer ?? undefined,
          serviceType: token.serviceType ?? undefined,
        });

        const info = await getConsentInfo(token);
        if (isError(info)) {
          return showBoundary(info);
        }
        console.log('Parsed info successfully as', info);

        setConsentContext({ token, info });
        setLoading(false);
        analyticsClient.sendConsentScreenEvent();
      })
      .catch(showBoundary);
  }, [showBoundary]);

  const setSelectedOrg = useCallback(
    async (orgId: string | null) => {
      if (!consentContext) return;
      const requestId = selectedOrgRequestId.current + 1;
      selectedOrgRequestId.current = requestId;
      setSelectedOrgState(orgId);
      setOrgError(null);
      // Reset scope selection immediately when org changes to prevent stale scopes
      // being submitted during the async /consent/info fetch window.
      // Will be repopulated with the new org's filteredScopes once the response arrives.
      setSelectedScopes(undefined);
      // Reset site selection only when org actually changes (not when adding more sites within same org)
      setSelectedWorkspace((prev) => {
        if (!orgId) return null;
        // Keep existing selection if same org; clear if org changed
        const prevOrg = prev
          ? consentContext.info.availableOrganizations
              ?.flatMap(
                (o) =>
                  o.availableWorkspaces?.map((s) => ({ s, orgId: o.orgId })) ??
                  [],
              )
              .find((x) => prev.split(',').includes(x.s))?.orgId
          : null;
        return prevOrg === orgId ? prev : null;
      });
      if (!orgId) {
        setIsOrgLoading(false);
        return;
      }

      setIsOrgLoading(true);
      try {
        const info = await getConsentInfo(consentContext.token, {
          selectedOrg: orgId,
          includeDisabledScopes: true,
        });
        if (selectedOrgRequestId.current !== requestId) return;
        if (isError(info)) {
          // TWG keeps selected-site "no scopes" policy failures inline so the
          // site selector remains visible and the user can try another site.
          if (
            consentContext.token.serviceType === 'TWG' &&
            info.error === 'no-scopes'
          ) {
            setOrgError(info);
          } else {
            showBoundary(info);
          }
          return;
        }
        console.log('Parsed info (with org) successfully as', info);
        setConsentContext((prev) =>
          prev
            ? {
                ...prev,
                info: {
                  // Retain availableOrganizations from the initial load if the
                  // org-scoped response omits them (so the dropdown stays populated).
                  availableOrganizations: prev.info.availableOrganizations,
                  ...info,
                },
              }
            : prev,
        );
        // Default user-selected scopes to the full allowed set for this org.
        // The user can then deselect individual scopes before accepting.
        setSelectedScopes(
          info.filteredScopes ?? consentContext.token.scopes ?? [],
        );
      } catch (e) {
        if (selectedOrgRequestId.current === requestId) {
          if (consentContext.token.serviceType === 'TWG') {
            setOrgError({
              error: 'unexpected',
              clientName: consentContext.token.clientName ?? undefined,
              description: 'Unexpected error',
            });
          } else {
            showBoundary(e);
          }
        }
      } finally {
        if (selectedOrgRequestId.current === requestId) {
          setIsOrgLoading(false);
        }
      }
    },
    [consentContext, showBoundary],
  );

  return (
    <>
      {isLoading && loading}
      {!isLoading && consentContext && (
        <ReactConsentContext.Provider
          value={{
            ...consentContext,
            selectedWorkspace,
            setSelectedWorkspace,
            acceptDisabled,
            setAcceptDisabled,
            selectedOrg,
            setSelectedOrg,
            isOrgLoading,
            orgError,
            selectedScopes,
            setSelectedScopes,
          }}
        >
          {children}
        </ReactConsentContext.Provider>
      )}
    </>
  );
};

export default ConsentContextProvider;
